<template>
  <section aria-labelledby="search-results-header">
    <h1
      id="search-results-header"
      class="mb-2 font-normal text-theme-600 flex items-center"
    >
      Search <template v-if="$route.query.q">results for "{{ $route.query.q }}"</template>
      <transition name="fade">
        <BaseIcon
          v-if="isLoading"
          class="spinner ml-4"
          icon="spinner"
          aria-hidden="true"
        />
      </transition>
    </h1>
    <SearchInput
      id="search-input"
      placeholder="Search . . ."
      :is-loading="isLoading"
      :value="searchQuery"
      data-test="inline-search"
      @input="handleSearch"
    />
    <div class="flex items-center justify-between mb-4">
      <p
        data-test="results-length"
        class="font-semibold text-theme-600 ui-font"
        role="status"
      >
        <template v-if="isLoading">
          Loading results
        </template>
        <template v-else-if="numberOfResults">
          Showing {{ resultsStart }} – {{ resultsEnd }} of {{ numberOfResults }} results
        </template>
        <template v-else>
          No Results
        </template>
      </p>
    </div>
    <section
      v-if="!isLoading"
      id="search-results"
      role="list"
      tabindex="0"
      aria-label="Search results list"
    >
      <div
        v-for="result of modifiedResults"
        :key="result.id"
        role="listitem"
        class="mb-4"
      >
        <NuxtLink
          :to="{ path: result.modifiedUrl }"
          :aria-labelledby="result.modifiedUrl"
          :title="result.name"
          class="text-left"
        >
          <span
            :id="result.url"
            class="mb-0 pb-0 text-xl block result-label"
          >
            {{ result.name }}
          </span>
          <span
            aria-hidden="true"
            class="result-url"
          >{{ `${host}${result.modifiedUrl}` }}</span>
        </NuxtLink>
        <p
          aria-hidden="true"
          class="whitespace-pre-line"
        >{{ getMatches(result.matches) }}</p>
      </div>
    </section>
    <BasePagination
      v-if="numberOfResults > pageSize"
      :current-page="currentPage"
      :total-pages="totalPages"
      data-test="pagination-test"
      @page-change="updatePage"
    />
  </section>
</template>

<script>
import { mapState } from 'vuex'
import striptags from 'striptags'
import SearchInput from '@/components/SearchInput'
import { debounce } from '@/utils/generalUtils'
import { LIMIT_MEDIUM } from '@/constants/pagination'

export default {
  components: { SearchInput },
  data() {
    return {
      results: [],
      numberOfResults: 0,
      isLoading: false,
      pageSize: LIMIT_MEDIUM,
      previousRoute: null,
      searchQuery: this.$route.query.q,
    }
  },
  computed: {
    ...mapState('settings', {
      activeCatalog: (state) => state.activeCatalog,
      items: (state) => state.nav,
    }),
    ...mapState('settings', ['school', 'host']),
    currentPage() {
      return this.$route.query.page ? parseInt(this.$route.query.page, 10) : 1
    },
    totalPages() {
      return Math.ceil(this.numberOfResults / LIMIT_MEDIUM)
    },
    resultsStart() {
      return (this.currentPage - 1) * LIMIT_MEDIUM
    },
    resultsEnd() {
      const end = this.resultsStart + LIMIT_MEDIUM
      return this.numberOfResults < end ? this.numberOfResults : end
    },
    modifiedResults() {
      return this.results.map((result) => ({
        ...result,
        modifiedUrl: this.modifyUrlPath(result),
      }))
    },
  },
  watch: {
    '$route.query': 'fetchData',
    '$route.query.q'() {
      this.searchQuery = this.$route.query.q
    },
  },
  // beforeRouteEnter(to, from, next) {
  //   if (to.query.q === '') {
  //     next({ name: 'index' })
  //   } else {
  //     next((vm) => {
  //       vm.previousRoute = from
  //     })
  //   }
  // },
  // beforeRouteUpdate(to, from, next) {
  //   const { q } = to.query
  //
  //   if (!q) {
  //     if (this.previousRoute) {
  //       next(this.previousRoute)
  //     } else {
  //       next({ name: 'index' })
  //     }
  //   } else {
  //     next()
  //   }
  // },
  mounted() {
    this.fetchData()
  },
  methods: {
    modifyUrlPath(result) {
      if (result.id.includes('department')) {
        const departmentCode = result.id.replace('department-', '') // we need the department code from the page id
        return `/departments/${encodeURIComponent(departmentCode)}/overview` // the actual department URL
      }
      return result.url
    },
    handleSearch(v) {
      this.searchQuery = v
      this.searchQuery ? this.searchDebounced(v) : this.search(v)
    },
    searchDebounced: debounce(function(v) {
      this.search()
    }, 500),
    search() {
      this.$router.push({
        name: 'search',
        query: {
          page: 1,
          ...this.$route.query,
          q: this.searchQuery,
        },
      })
    },
    updatePage(page) {
      this.$router.push({
        name: 'search',
        query: {
          ...this.$route.query,
          page,
        },
      })
    },
    async fetchData() {
      this.isLoading = true
      const { q, page } = this.$route.query

      const limit = LIMIT_MEDIUM
      const skip = Math.max(0, page - 1) * LIMIT_MEDIUM

      const url = `/ca/${this.school}/search/catalogPages/${
        this.activeCatalog
      }/`

      try {
        const { results, numberOfResults } = await this.$axios.$get(url, {
          params: { limit, skip, q },
        })
        this.results = results
        this.numberOfResults = numberOfResults
        this.isLoading = false
      } catch (e) {
        this.$bugsnag.notify(e)
        console.warn('Error while fetching search results.')
      }
    },
    getMatches(matches) {
      return matches.reduce((text, match) => {
        let content = striptags(match.value, [], '\n')
          .replace(/\n\s*\n/g, '\n')
          .trim()
        const matchIndex = content.indexOf(this.$route.query.q)
        const startIndex = matchIndex >= 0 ? matchIndex : 0

        if (text.length) text += '; '

        if (content.length > 160) {
          content =
            content.substring(startIndex, startIndex + 160).trim() + '...'
        }

        return text + content
      }, '')
    },
  },
}
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
<style scoped>
.result-url,
.result-label {
  font-family: var(--textFontTypeface, inherit);
}
.result-url {
  color: var(--textFontColor, var(--text-gray-900, inherit));
  font-weight: var(--textFontWeight, inherit);
}
.result-label {
  color: var(--textFontColor, var(--text-theme-500, inherit));
  font-weight: var(--textFontWeight, 700);
}
</style>
